// PW-2 — "Keep this on your phone / desktop": one control for every portal. It swaps the page's
// manifest to the portal's own (so the pinned icon opens here, under the firm's name), then
// offers the install the way this browser allows: one tap on Android/Chrome/Edge, the Share →
// Add to Home Screen hint on iPhone/iPad, the File → Add to Dock hint on Safari for Mac. Hidden
// when already installed, on a browser that cannot install, on the first visit, or for 30 days
// after "Not now".
import React, { useEffect, useMemo, useState } from 'react';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
const VISITS_KEY = (k) => `keymade-portal-visits-${k}`;
const SNOOZE_KEY = (k) => `keymade-portal-keep-snoozed-${k}`;
// One visit per page load, however often the head mounts (StrictMode, loading → loaded).
const COUNTED = new Map();
function useManifest(href, title, iconHref) {
  useEffect(() => {
    if (!href) return undefined;
    const link = document.querySelector('link[rel="manifest"]');
    const prev = link ? link.getAttribute('href') : null;
    if (link) link.setAttribute('href', href);
    const metaTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const prevTitle = metaTitle ? metaTitle.getAttribute('content') : null;
    if (metaTitle && title) metaTitle.setAttribute('content', title);
    const touch = document.querySelector('link[rel="apple-touch-icon"]');
    const prevIcon = touch ? touch.getAttribute('href') : null;
    if (touch && iconHref) touch.setAttribute('href', iconHref);
    return () => {
      if (link && prev) link.setAttribute('href', prev);
      if (metaTitle && prevTitle) metaTitle.setAttribute('content', prevTitle);
      if (touch && prevIcon) touch.setAttribute('href', prevIcon);
    };
  }, [href, title, iconHref]);
}
export default function KeepPortal({ portalKey, manifestHref, title, iconHref, className = '', buttonClass = '' }) {
  const { installed, isIOS, isMacSafari, canPromptAutomatically, promptInstall } = useInstallPrompt();
  useManifest(manifestHref, title, iconHref);
  const [visits, setVisits] = useState(0);
  const [snoozed, setSnoozed] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    try {
      let n = COUNTED.get(portalKey);
      if (n === undefined) {
        n = parseInt(localStorage.getItem(VISITS_KEY(portalKey)) || '0', 10) + 1;
        localStorage.setItem(VISITS_KEY(portalKey), String(n));
        COUNTED.set(portalKey, n);
      }
      setVisits(n);
      const until = parseInt(localStorage.getItem(SNOOZE_KEY(portalKey)) || '0', 10);
      setSnoozed(Date.now() < until);
    } catch (_e) { setVisits(2); }
  }, [portalKey]);
  const phone = useMemo(() => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || navigator.maxTouchPoints > 1, []);
  const can = installed ? false : (isIOS || isMacSafari || canPromptAutomatically);
  if (!can || visits < 2 || snoozed) return null;
  const label = phone ? 'Keep this on your phone' : 'Keep this on your desktop';
  const snooze = () => { try { localStorage.setItem(SNOOZE_KEY(portalKey), String(Date.now() + 30 * 86400000)); } catch (_e) {} setSnoozed(true); setOpen(false); };
  const go = async () => {
    if (canPromptAutomatically) { const r = await promptInstall(); if (r.accepted) setOpen(false); return; }
    setOpen(true);
  };
  return (
    <div className={`kpk ${className}`} data-testid="keep-portal">
      <button type="button" className={buttonClass || 'kpk-btn'} onClick={go} data-testid="keep-portal-button">{label}</button>
      {open ? (
        <div className="kpk-hint" role="dialog" aria-label={label} data-testid="keep-portal-hint">
          {isIOS ? (
            <p>Tap <b>Share</b> (the square with the arrow), then <b>Add to Home Screen</b>. It opens straight here from then on.</p>
          ) : isMacSafari ? (
            <p>In Safari's <b>File</b> menu choose <b>Add to Dock</b>. It opens straight here from then on.</p>
          ) : (
            <p>Use your browser's menu and choose <b>Install</b> or <b>Add to Home Screen</b>.</p>
          )}
          <div className="kpk-acts">
            <button type="button" className={buttonClass || 'kpk-btn'} onClick={() => setOpen(false)} data-testid="keep-portal-done">Done</button>
            <button type="button" className={buttonClass || 'kpk-btn'} onClick={snooze} data-testid="keep-portal-later">Not now</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
